import { CloseOutlined, MoreOutlined } from '@ant-design/icons';
import { Card, Dropdown, Image, Menu, message, Modal } from 'antd';
import dayjs from 'dayjs';
import FileViewer from 'features/new-clients/components/clients-details/file-viewer';
import { MessageCircle } from 'lucide-react';
import { formatUtcToLocal, getFileExtension } from 'routes/utils';

import { StatusBadge } from './StatusBadge';

interface DocumentCardProps {
  document: any;
  onAddComment: (document: any) => void;
}

export const DocumentCard = ({ document, onAddComment }: DocumentCardProps) => {
  const handleMenuClick = ({ key }: any) => {
    if (key === 'download') {
      message.success(`Downloading ${document.documentName}`);
      window.open(document.documentPath, '_blank');
      const link = document.createElement('a');
      link.href = document.documentPath;
      link.download = document.documentName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (key === 'preview') {
      // Open the preview in a new tab
      Modal.info({
        title: 'Document View',
        width: '100%',
        icon: null,
        closable: true,
        closeIcon: <CloseOutlined />,
        style: { top: '0px', maxWidth: '500px' },
        content: (
          <div style={{ textAlign: 'start' }}>
            {(() => {
              const extension = getFileExtension(document.documentPath);

              if (['jpg', 'jpeg', 'png', 'gif'].includes(extension!)) {
                // If it's an image, display it directly
                return <Image src={document.documentPath} width='100%' />;
              } else if (
                [
                  'doc',
                  'docx',
                  'xls',
                  'xlsx',
                  'ppt',
                  'pptx',
                  'csv',
                  'pdf',
                ].includes(extension!)
              ) {
                // Use Google Docs Viewer for documents
                return (
                  <iframe
                    src={`https://docs.google.com/gview?url=${document.documentPath}&embedded=true`}
                    width='100%'
                    height='500'
                    style={{ border: 'none' }}
                    title='Document Preview'
                  />
                );
              } else {
                return (
                  <iframe
                    src={`${document.documentPath}`}
                    width='100%'
                    height='500'
                    style={{ border: 'none' }}
                    title='Document Preview'
                  />
                );
              }
            })()}
          </div>
        ),
        okButtonProps: { style: { display: 'none' } },
      });
    } else if (key === 'Add Comment') {
      onAddComment(document);
    }
  };

  const renderMenu = () => (
    <Menu
      onClick={({ key }) => handleMenuClick({ key })}
      items={[
        { label: 'Download', key: 'download' },
        { label: 'Preview', key: 'preview' },
        { label: 'Add Comment', key: 'Add Comment' },
      ]}
    />
  );

  return (
    <Card
      role='button'
      className='shadow-sm hover:bg-gray-50'
      style={{ width: '100%', position: 'relative' }}
      bodyStyle={{ padding: '16px' }}
    >
      <div className='flex gap-3'>
        {/* <Image
          src={document.documentPath}
          alt={document.documentName}
          width={80}
          height={80}
          className='object-cover mr-4 border border-gray-300 rounded-lg shadow-md hover:shadow-md transition-shadow duration-200'
        /> */}
        <div className='bg-white p-2 rounded-xl relative'>
          <figure className='mediafolder'>
            <FileViewer documentPath={document.documentPath} />
          </figure>
        </div>

        <div className='flex flex-col grow'>
          <div className='flex justify-between items-center'>
            <p className='font-bold'>{document?.documentName}</p>
            <div className='flex align-middle'>
              <dt className='stsbx'>
                <StatusBadge status={document.documentStatus} />
              </dt>

              <dt className='mnubdrp'>
                <Dropdown overlay={renderMenu()} trigger={['click']}>
                  <MoreOutlined
                    style={{ fontSize: '24px', cursor: 'pointer' }}
                  />
                </Dropdown>
              </dt>
            </div>
          </div>
          {document.documentSize != null && (
            <dt className='tmrbx'>Size : {document.documentSize}</dt>
          )}
          <dt className='tmrbx'>
            <MessageCircle /> {`${document?.totalComments} Comments`}
          </dt>
          <dt className='tmrbx text-xs text-gray-500'>
            Last Update:{' '}
            {/* {dayjs(document.lastUpdatedDate).format('DD MMM YYYY | hh:mm A')} */}
            {formatUtcToLocal(
              dayjs(document?.lastUpdatedDate).format('DD-MMM-YYYY'),
            )}
          </dt>
        </div>
      </div>
    </Card>
  );
};
